import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Phone, ShieldCheck, ArrowRight, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function PhoneRegistrationModal() {
  const { profile, updateProfile } = useAuth();
  const [displayName, setDisplayName] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Show modal if user is logged in but has no phone number or no display name
  const isOpen = !!profile && (!profile.phoneNumber || !profile.displayName || profile.displayName.trim() === '');

  React.useEffect(() => {
    if (profile) {
      if (!displayName && profile.displayName) {
        setDisplayName(profile.displayName);
      }
      if (!phoneNumber && profile.phoneNumber) {
        let num = profile.phoneNumber;
        if (num.startsWith('+62')) {
          num = num.substring(3);
        } else if (num.startsWith('62')) {
          num = num.substring(2);
        } else if (num.startsWith('0')) {
          num = num.substring(1);
        }
        setPhoneNumber(num);
      }
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName || displayName.trim().length < 2) {
      toast.error("Harap masukkan Nama Lengkap yang valid.");
      return;
    }
    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error("Harap masukkan nomor WhatsApp yang valid.");
      return;
    }

    setIsSubmitting(true);
    const formattedPhone = phoneNumber.startsWith('+62') 
      ? phoneNumber 
      : phoneNumber.startsWith('62') 
        ? `+${phoneNumber}` 
        : phoneNumber.startsWith('0')
          ? `+62${phoneNumber.substring(1)}`
          : `+62${phoneNumber}`;

    try {
      await updateProfile({ 
        displayName: displayName.trim(), 
        phoneNumber: formattedPhone 
      });
      toast.success("Profil Anda berhasil dilengkapi!");
    } catch (error) {
      toast.error("Gagal menyimpan profil. Silakan coba lagi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md bg-card border-none shadow-2xl rounded-3xl overflow-hidden p-0">
        <div className="bg-primary p-8 text-primary-foreground text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="relative z-10">
            <div className="h-16 w-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gold/30">
              <User className="h-8 w-8 text-primary-foreground" />
            </div>
            <DialogTitle className="text-2xl font-bold text-primary-foreground mb-2">Lengkapi Profil Anda</DialogTitle>
            <DialogDescription className="text-primary-foreground/60 text-sm">
              Satu langkah lagi untuk memulai. Masukkan Nama Lengkap dan nomor WhatsApp aktif Anda untuk menyelesaikan pendaftaran.
            </DialogDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-foreground/60 font-bold text-xs uppercase tracking-widest">
              Nama Lengkap
            </Label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40 font-bold">
                <User className="h-5 w-5" />
              </div>
              <Input 
                id="displayName"
                type="text"
                placeholder="Masukkan nama lengkap Anda"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="pl-12 h-14 rounded-xl border-border focus:border-primary focus:ring-primary font-medium text-base bg-card text-foreground"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-foreground/60 font-bold text-xs uppercase tracking-widest">
              Nomor WhatsApp Aktif
            </Label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40 font-bold border-r border-border pr-3">
                +62
              </div>
              <Input 
                id="phone"
                type="tel"
                placeholder="8123456789"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                className="pl-16 h-14 rounded-xl border-border focus:border-primary focus:ring-primary font-bold text-lg bg-card text-foreground"
                required
              />
            </div>
            <p className="text-[10px] text-foreground/40 italic">
              * Contoh: 8123456789 (tanpa angka 0 atau +62 di depan)
            </p>
          </div>

          <div className="p-4 bg-muted rounded-2xl border border-border flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
            <p className="text-xs text-foreground/60 leading-relaxed">
              Data Anda aman bersama kami. Kami hanya menggunakan informasi ini untuk keperluan verifikasi dan koordinasi proyek di Azman204.
            </p>
          </div>

          <Button 
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-14 rounded-xl text-lg shadow-xl shadow-primary/20 group"
          >
            {isSubmitting ? "Menyimpan..." : (
              <>
                Selesaikan Pendaftaran
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
